import React, { memo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';

interface CalledEntry {
  position: number;
  customer_name?: string;
  counter?: string | null;
}

interface QueueData {
  id: string;
  name: string;
  description: string;
  currentLength: number;
  estimatedServiceTime: number;
  status: 'active' | 'inactive' | 'maintenance';
  lastUpdated: string;
  // `counter` is the multi-counter service point the customer was directed to
  // (e.g. "Counter 3" / "Billing"), or null/absent when not in use.
  currentlyServing?: { position: number; customer_name: string; counter?: string | null } | null;
  /** One entry per counter in use — several people are served at once. */
  nowServing?: CalledEntry[];
  /** Server-computed: already divided by the service points working the queue. */
  estimatedWaitTime?: number;
  recentlyCalled?: CalledEntry[];
  activeCounters?: number;
  joinQr?: string | null;
}

/**
 * How much room this card has on the board. Fewer queues on screen → bigger
 * card → bigger type, so a lone queue's token number is readable from the
 * back of the store instead of floating in dead space.
 */
export type CardDensity = 'md' | 'lg' | 'xl';

interface QueueCardProps {
  queue: QueueData;
  density?: CardDensity;
}

/** Counters open at once before the compact one-line-per-counter layout. */
export const COMPACT_COUNTER_MIN = 3;
/** Counters open at once before the card takes the full width of the board. */
export const WIDE_COUNTER_MIN = 5;

/** How many counters this queue currently has customers at. */
export const servingCount = (q: { nowServing?: unknown[]; currentlyServing?: unknown }) =>
  (q.nowServing && q.nowServing.length) ? q.nowServing.length : (q.currentlyServing ? 1 : 0);

const formatWaitTime = (totalMinutes: number) => {
  if (totalMinutes <= 0) return 'No wait';
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const QueueCard: React.FC<QueueCardProps> = ({ queue, density = 'md' }) => {
  // Type scale for the card's key figures. xs values stay fixed — phones never
  // show fewer, larger cards; this is purely for TV boards with spare room.
  const scale = density === 'xl' ? 1.9 : density === 'lg' ? 1.15 : 1;
  const fs = (rem: number) => `${(rem * scale).toFixed(2)}rem`;
  // Prefer the full set. With two counters working in parallel, showing only
  // the most recent call left the other customer — actually at a counter being
  // served — sitting under "JUST CALLED".
  const serving = (queue.nowServing && queue.nowServing.length
    ? queue.nowServing
    : queue.currentlyServing
    ? [queue.currentlyServing]
    : []) as CalledEntry[];
  const recent = (queue.recentlyCalled || []).slice(0, 4);
  // Past two counters the big-number treatment no longer fits a card.
  const isCompact = serving.length >= COMPACT_COUNTER_MIN;
  // Beyond a handful, squeezing the list into a third of the board makes it
  // unreadable. Spanning the full width turns 25 entries into ~5 short rows.
  const isWide = serving.length >= WIDE_COUNTER_MIN;
  const isOpen = queue.status === 'active';

  // Fall back to the old client-side estimate only if the server didn't send
  // one (older payload) — that form assumes a single service point.
  const waitMinutes =
    queue.estimatedWaitTime ?? queue.currentLength * queue.estimatedServiceTime;

  return (
    <Card
      sx={{
        height: '100%',
        gridColumn: isWide ? '1 / -1' : undefined,
        backgroundColor: 'background.paper',
        border: '2px solid',
        borderColor: isOpen ? 'success.main' : 'grey.600',
        transition: 'border-color 0.3s ease',
        opacity: isOpen ? 1 : 0.65,
      }}
    >
      <CardContent
        sx={{
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        {/* Everything above the waiting/wait footer. If the card ever runs out
            of room (small TV, warning banner shown), this middle section clips
            — the footer must never be what disappears. */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Queue name + join QR */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 500,
                fontSize: { xs: '1.5rem', md: fs(1.85) },
                // Two lines reserved either way, so the block below starts at
                // the same height on every card regardless of name length.
                lineHeight: 1.2,
                minHeight: '2.4em',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {queue.name}
            </Typography>
            {/* Only surfaced when NOT open — "ACTIVE" on every card told the
                viewer nothing, but "CLOSED" stops someone waiting for nothing. */}
            {!isOpen && (
              <Chip
                label={queue.status === 'maintenance' ? 'TEMPORARILY CLOSED' : 'CLOSED'}
                color="warning"
                size="small"
                sx={{ fontWeight: 'bold', mt: 0.5 }}
              />
            )}
          </Box>

          {queue.joinQr && isOpen && (
            <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
              <Box
                component="img"
                src={queue.joinQr}
                alt=""
                sx={{
                  width: { xs: 64, md: Math.round(84 * scale) },
                  height: { xs: 64, md: Math.round(84 * scale) },
                  display: 'block',
                  borderRadius: 1,
                  backgroundColor: '#fff',
                  p: 0.5,
                }}
              />
              <Typography sx={{ mt: 0.5, opacity: 0.6, fontSize: '0.7rem', letterSpacing: '0.04em' }}>
                SCAN TO JOIN
              </Typography>
            </Box>
          )}
        </Box>

        {/* NOW SERVING — the one thing everyone already in the queue is here
            to read, so it gets the largest type on the card. */}
        <Box
          sx={{
            textAlign: 'center',
            py: scale > 1.5 ? 4 : 2,
            px: 1,
            borderRadius: 2,
            backgroundColor: serving.length ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.04)',
            border: '1px solid',
            borderColor: serving.length ? 'success.main' : 'transparent',
          }}
        >
          <Typography
            sx={{
              opacity: 0.7,
              fontSize: { xs: '0.85rem', md: fs(1) },
              letterSpacing: '0.12em',
              fontWeight: 500,
            }}
          >
            NOW SERVING
          </Typography>
          {serving.length > 0 ? (
            isCompact ? (
              /* A busy hall (3+ counters at once). Stacked big numbers wrapped
                 into a block tall enough to push the card off a 1080p screen —
                 measured at 664px against a 453px card. One line per counter in
                 a responsive grid keeps every customer visible and the card the
                 same height whether 3 counters are open or 25. */
              <Box
                sx={{
                  mt: 1,
                  display: 'grid',
                  gridTemplateColumns: isWide
                    ? 'repeat(auto-fit, minmax(150px, 1fr))'
                    : 'repeat(auto-fit, minmax(130px, 1fr))',
                  gap: 0.75,
                }}
              >
                {serving.map((s, i) => (
                  <Box
                    key={`${s.position}-${s.counter ?? i}`}
                    sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.75, minWidth: 0 }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: 'success.main',
                        fontSize: { xs: '1.5rem', md: fs(1.9) },
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {s.position}
                    </Typography>
                    {s.counter && (
                      <Typography
                        sx={{
                          fontSize: { xs: '0.8rem', md: '0.9rem' },
                          color: 'info.main',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {s.counter}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: serving.length > 1 ? 3 : 0,
              }}
            >
              {serving.map((s, i) => (
                <Box key={`${s.position}-${s.counter ?? i}`} sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      color: 'success.main',
                      lineHeight: 1.05,
                      fontSize: serving.length > 1
                        ? { xs: '2.75rem', md: fs(3.75) }
                        : { xs: '3.5rem', md: fs(5) },
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {s.position}
                  </Typography>
                  {(s.counter || s.customer_name) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      {s.customer_name && (
                        <Typography sx={{ fontSize: { xs: '1.1rem', md: fs(1.35) }, opacity: 0.9 }}>
                          {s.customer_name}
                        </Typography>
                      )}
                      {s.counter && (
                        <Chip
                          label={`→ ${s.counter}`}
                          sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: '0.95rem', md: fs(1.1) },
                            height: 'auto',
                            py: 0.75,
                            color: 'info.main',
                            backgroundColor: 'rgba(33,150,243,0.15)',
                            border: '1px solid',
                            borderColor: 'info.main',
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            )
          ) : (
            <Typography
              sx={{
                opacity: 0.45,
                lineHeight: 1.05,
                fontSize: { xs: '3.5rem', md: fs(5) },
                fontWeight: 300,
              }}
            >
              —
            </Typography>
          )}
        </Box>

        {/* Recently called — rescues anyone who looked away for a moment. */}
        {recent.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Typography sx={{ opacity: 0.55, fontSize: { xs: '0.8rem', md: fs(0.9) }, letterSpacing: '0.06em' }}>
              JUST CALLED
            </Typography>
            {recent.map((entry, i) => (
              <Chip
                key={`${entry.position}-${i}`}
                label={entry.counter ? `${entry.position} → ${entry.counter}` : entry.position}
                size={scale > 1 ? 'medium' : 'small'}
                sx={{
                  opacity: 0.8,
                  fontWeight: 600,
                  fontSize: { md: fs(0.8125) },
                  fontVariantNumeric: 'tabular-nums',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                }}
              />
            ))}
          </Box>
        )}

        </Box>

        {/* Waiting / wait estimate — useful to someone deciding whether to
            join, secondary to everyone already in the queue. Pinned to the
            bottom (the flex block above absorbs spare height) so it lines up
            across cards and never gets clipped. */}
        <Box sx={{ flexShrink: 0 }}>
          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'baseline', gap: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  fontSize: { xs: '1.6rem', md: fs(2) },
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {queue.currentLength}
              </Typography>
              <Typography sx={{ opacity: 0.7, fontSize: { xs: '0.85rem', md: fs(0.95) } }}>
                Waiting
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'secondary.main',
                  fontSize: { xs: '1.6rem', md: fs(2) },
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatWaitTime(waitMinutes)}
              </Typography>
              <Typography sx={{ opacity: 0.7, fontSize: { xs: '0.85rem', md: fs(0.95) } }}>
                If you join now
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const sameCalledList = (a?: CalledEntry[], b?: CalledEntry[]) => {
  const x = a || [];
  const y = b || [];
  return x.length === y.length && x.every((e, i) => e.position === y[i]?.position && e.counter === y[i]?.counter);
};

// Memoized: only re-render when the displayed data actually changes.
export default memo(QueueCard, (prevProps, nextProps) => {
  return (
    prevProps.density === nextProps.density &&
    prevProps.queue.id === nextProps.queue.id &&
    prevProps.queue.name === nextProps.queue.name &&
    prevProps.queue.currentLength === nextProps.queue.currentLength &&
    prevProps.queue.estimatedServiceTime === nextProps.queue.estimatedServiceTime &&
    prevProps.queue.estimatedWaitTime === nextProps.queue.estimatedWaitTime &&
    prevProps.queue.status === nextProps.queue.status &&
    prevProps.queue.joinQr === nextProps.queue.joinQr &&
    prevProps.queue.currentlyServing?.position === nextProps.queue.currentlyServing?.position &&
    prevProps.queue.currentlyServing?.customer_name === nextProps.queue.currentlyServing?.customer_name &&
    prevProps.queue.currentlyServing?.counter === nextProps.queue.currentlyServing?.counter &&
    sameCalledList(prevProps.queue.nowServing, nextProps.queue.nowServing) &&
    sameCalledList(prevProps.queue.recentlyCalled, nextProps.queue.recentlyCalled)
  );
});
